import { type RefObject, useEffect, useRef, useState } from "react";
import { BackwardIcon, ForwardIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";

export interface CenterPlaybackButtonProps {
  /** Increments / toggles whenever the orchestrator wants to flash the overlay. */
  ping: boolean;
  /**
   * Read inside the ping handler to figure out which rate to display. Ref-based
   * because we want the *current* temp rate at the moment of the ping, not the
   * one captured by closure on the last render.
   */
  tempPlaybackRateRef: RefObject<number>;
}

/**
 * The "8x" overlay that briefly flashes over the video when the user ramps
 * playback rate via JKL. Hidden by default; appears on each `ping` change and
 * fades out after 600ms.
 */
export function CenterPlaybackButton({
  ping,
  tempPlaybackRateRef,
}: CenterPlaybackButtonProps) {
  const [rate, setRate] = useState<number>(0);
  const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTempPlaybackRate = useRef<number | null>(null);
  const [initialRender, setInitialRender] = useState<boolean>(true);

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
      prevTempPlaybackRate.current = tempPlaybackRateRef.current;
      return;
    }
    if (timeoutHandleRef.current) clearTimeout(timeoutHandleRef.current);
    if (prevTempPlaybackRate.current !== null) {
      setRate(tempPlaybackRateRef.current);
      timeoutHandleRef.current = setTimeout(() => setRate(0), 600);
    }
    prevTempPlaybackRate.current = tempPlaybackRateRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ping]);

  return (
    <AnimatePresence>
      {!!rate && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex size-[90px] flex-col items-center justify-center rounded-full bg-black/60 font-bold text-gray-200 text-sm"
          >
            {rate > 0 ? (
              <ForwardIcon className="h-6 w-6" />
            ) : (
              <BackwardIcon className="h-6 w-6" />
            )}
            <div className="mt-1">{rate}x</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
